import React from 'react';
import { Card, Chip, cn } from '@heroui/react';
import type { trpcProxyClient } from '@/lib/trpc';
import { useMobileNavigate } from '@/hooks/useMobileNavigate';

type ActivityStatusChipColor = 'accent' | 'default' | 'success';

type ActivityStatusChip = {
  label: '报名中' | '报名已结束' | '进行中' | '已结束';
  chipColor: ActivityStatusChipColor;
};

export type ActivityOutput = Awaited<
  ReturnType<typeof trpcProxyClient.activities.list.query>
>['items'][number];

export interface ActivityItemProps {
  /**
   * 活动数据
   */
  activity: ActivityOutput;
  /**
   * 自定义类名
   */
  className?: string;
}

function parseDateTime(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const d = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatActivityDateTime(value: Date | string | null | undefined): string {
  const date = parseDateTime(value);
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  const weekday = weekdays[date.getDay()];
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${month}.${day}（${weekday}）${hours}:${minutes}`;
}

function formatActivityTimeRange(
  startTime: Date | string | null | undefined,
  endTime: Date | string | null | undefined
): string {
  const start = formatActivityDateTime(startTime);
  const end = formatActivityDateTime(endTime);
  if (!start && !end) return '';
  if (!start) return ` - ${end}`;
  if (!end) return `${start} - `;
  return `${start} - ${end}`;
}

function getActivityStatusChip(activity: ActivityOutput): ActivityStatusChip | null {
  const now = new Date();
  const signupEnd = parseDateTime(activity.signupEndTime);
  const startTime = parseDateTime(activity.startTime);
  const endTime = parseDateTime(activity.endTime);

  // 4) 已结束：endTime 已过
  if (endTime && now > endTime) {
    return { label: '已结束', chipColor: 'default' };
  }
  // 3) 进行中：startTime 已开始
  if (startTime && now >= startTime) {
    return { label: '进行中', chipColor: 'success' };
  }
  // 2) 报名已结束：signupEnd 已过且未到 startTime
  if (signupEnd && now > signupEnd && (!startTime || now < startTime)) {
    return { label: '报名已结束', chipColor: 'default' };
  }
  // 1) 报名中：signupEnd 存在且尚未到截止
  if (signupEnd && now < signupEnd) {
    return { label: '报名中', chipColor: 'accent' };
  }
  return null;
}

/**
 * 活动列表项组件（HeroUI Card + Chip）
 *
 * 显示内容：
 * 1. 封面图（完整呈现）
 * 2. 标题
 * 3. 活动时间（MM.DD（周*）HH:MM-MM.DD（周*）HH:MM）
 * 4. 活动状态 Chip
 */
export function ActivityItem({
  activity,
  className,
}: ActivityItemProps) {
  const { goWithBack } = useMobileNavigate();

  const handleClick = () => {
    goWithBack(`/activities/${activity.id}`);
  };

  const activityTimeRange = formatActivityTimeRange(
    activity.startTime,
    activity.endTime
  );
  const statusChip = getActivityStatusChip(activity);

  return (
    <Card
      variant="default"
      className={cn(
        'cursor-pointer overflow-hidden rounded-xl border border-default-200 p-0 shadow-sm transition-all hover:shadow-md active:scale-[0.98]',
        className
      )}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="relative w-full aspect-[16/9] bg-default-100 overflow-hidden">
        {activity.coverImage ? (
          <img
            src={activity.coverImage}
            alt={activity.title || '活动封面'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-default-500">
            暂无封面
          </div>
        )}
      </div>

      {(activity.title || statusChip) && (
        <Card.Header className="flex flex-row items-start justify-between gap-3 px-4 pt-4 pb-2">
          {activity.title ? (
            <Card.Title className="mt-0 min-w-0 flex-1 pr-1 text-base font-semibold leading-5 line-clamp-2">
              {activity.title}
            </Card.Title>
          ) : (
            <div className="min-w-0 flex-1" />
          )}
          {statusChip ? (
            <Chip className="shrink-0 self-start" color={statusChip.chipColor}>
              <Chip.Label>{statusChip.label}</Chip.Label>
            </Chip>
          ) : null}
        </Card.Header>
      )}

      {activityTimeRange ? (
        <Card.Content className="px-4 pb-4 pt-0">
          <Card.Description className="text-sm text-default-500 line-clamp-none">
            活动时间: {activityTimeRange}
          </Card.Description>
        </Card.Content>
      ) : null}
    </Card>
  );
}
